import CrudSection from "../components/CrudSection";
import FeatureDashboard from "../../../components/base/FeatureDashboard";
import { StatusTextBadge } from "../../../components/base/StatusTextBadge";
import type { CrudColumn, CrudField } from "../types";

// ===========================================================================
// Comunicación y engagement — bespoke sections.
// ===========================================================================

export function ComunicacionDashboard() {
  return (
    <FeatureDashboard noticeKey="nuvexAccess.mockNotice"
      title="Comunicación y engagement"
      subtitle="Anuncios, notificaciones y segmentación"
      icon="ri-megaphone-line"
      stats={[
        { label: "Anuncios este mes", value: "18", icon: "ri-megaphone-line" },
        { label: "Notificaciones enviadas", value: "9,420", icon: "ri-notification-3-line", change: "+12%", positive: true },
        { label: "Tasa de apertura", value: "63%", icon: "ri-mail-open-line", change: "+4%", positive: true },
        { label: "Segmentos", value: "11", icon: "ri-group-2-line" },
      ]}
      chart={{
        title: "Mensajes enviados (semana)",
        data: [
          { name: "Lun", value: 320 }, { name: "Mar", value: 410 }, { name: "Mié", value: 280 },
          { name: "Jue", value: 520 }, { name: "Vie", value: 610 }, { name: "Sáb", value: 180 }, { name: "Dom", value: 90 },
        ],
      }}
      recent={{
        title: "Comunicados recientes",
        items: [
          { primary: "Mantenimiento de agua programado", secondary: "Enviado · 1,204 destinatarios", badge: "Enviado", tone: "ok" },
          { primary: "Asamblea mensual", secondary: "Programado · Torre A", badge: "Programado", tone: "warn" },
          { primary: "Recordatorio de cuotas", secondary: "Borrador", badge: "Borrador", tone: "muted" },
        ],
      }}
    />
  );
}

// --- Anuncios y avisos -----------------------------------------------------
interface Anuncio extends Record<string, unknown> {
  id: string; titulo: string; segmento: string; fecha: string; estado: string;
}
const anuncioColumns: CrudColumn<Anuncio>[] = [
  { key: "titulo", label: "Título" },
  { key: "segmento", label: "Segmento" },
  { key: "fecha", label: "Fecha" },
  { key: "estado", label: "Estado", align: "center", render: (r) => <StatusTextBadge value={r.estado} /> },
];
const anuncioFields: CrudField[] = [
  { key: "titulo", label: "Título", required: true, placeholder: "Mantenimiento de agua" },
  { key: "segmento", label: "Segmento", type: "select", defaultValue: "Toda la comunidad", options: [
    { value: "Toda la comunidad", label: "Toda la comunidad" }, { value: "Torre A", label: "Torre A" },
    { value: "Propietarios", label: "Propietarios" }, { value: "Miembros", label: "Miembros" },
  ] },
  { key: "fecha", label: "Fecha", type: "date", required: true },
  { key: "estado", label: "Estado", type: "select", defaultValue: "Borrador", options: [
    { value: "Borrador", label: "Borrador" }, { value: "Programado", label: "Programado" }, { value: "Enviado", label: "Enviado" },
  ] },
  { key: "cuerpo", label: "Contenido", type: "textarea", placeholder: "Mensaje del anuncio..." },
];
const anuncioSeed: Anuncio[] = [
  { id: "an1", titulo: "Mantenimiento de agua programado", segmento: "Toda la comunidad", fecha: "2026-06-09", estado: "Enviado" },
  { id: "an2", titulo: "Asamblea mensual", segmento: "Torre A", fecha: "2026-06-15", estado: "Programado" },
  { id: "an3", titulo: "Recordatorio de cuotas", segmento: "Propietarios", fecha: "2026-06-12", estado: "Borrador" },
];
export const AnunciosSection = () => (
  <CrudSection<Anuncio> title="Anuncios y avisos" subtitle="Comunicados oficiales a la comunidad"
    icon="ri-megaphone-line" columns={anuncioColumns} fields={anuncioFields} seed={anuncioSeed} searchKey="titulo" />
);

// --- Notificaciones push ---------------------------------------------------
interface Notificacion extends Record<string, unknown> {
  id: string; mensaje: string; canal: string; destinatarios: string; estado: string;
}
const notifColumns: CrudColumn<Notificacion>[] = [
  { key: "mensaje", label: "Mensaje" },
  { key: "canal", label: "Canal" },
  { key: "destinatarios", label: "Destinatarios", align: "right" },
  { key: "estado", label: "Estado", align: "center", render: (r) => <StatusTextBadge value={r.estado} /> },
];
const notifFields: CrudField[] = [
  { key: "mensaje", label: "Mensaje", required: true, placeholder: "Tu paquete llegó a caseta" },
  { key: "canal", label: "Canal", type: "select", defaultValue: "Push", options: [
    { value: "Push", label: "Push (app)" }, { value: "SMS", label: "SMS" }, { value: "Email", label: "Email" },
  ] },
  { key: "destinatarios", label: "Destinatarios", placeholder: "Depto 402 / Segmento" },
  { key: "estado", label: "Estado", type: "select", defaultValue: "Pendiente", options: [
    { value: "Pendiente", label: "Pendiente" }, { value: "Enviado", label: "Enviado" },
  ] },
];
const notifSeed: Notificacion[] = [
  { id: "n1", mensaje: "Tu paquete llegó a caseta", canal: "Push", destinatarios: "Depto 402", estado: "Enviado" },
  { id: "n2", mensaje: "Visita en espera", canal: "Push", destinatarios: "Casa 12", estado: "Enviado" },
  { id: "n3", mensaje: "Corte de luz programado", canal: "SMS", destinatarios: "1,204", estado: "Pendiente" },
];
export const NotificacionesSection = () => (
  <CrudSection<Notificacion> title="Notificaciones push" subtitle="Avisos instantáneos a la app"
    icon="ri-notification-3-line" columns={notifColumns} fields={notifFields} seed={notifSeed} searchKey="mensaje" />
);

// --- Segmentos -------------------------------------------------------------
interface Segmento extends Record<string, unknown> {
  id: string; nombre: string; criterio: string; miembros: string;
}
const segmentoColumns: CrudColumn<Segmento>[] = [
  { key: "nombre", label: "Segmento" },
  { key: "criterio", label: "Criterio" },
  { key: "miembros", label: "Miembros", align: "right" },
];
const segmentoFields: CrudField[] = [
  { key: "nombre", label: "Nombre", required: true, placeholder: "Torre A" },
  { key: "criterio", label: "Criterio", required: true, placeholder: "Unidad / rol / membresía" },
  { key: "miembros", label: "Miembros (estimado)", type: "number", defaultValue: 0 },
];
const segmentoSeed: Segmento[] = [
  { id: "sg1", nombre: "Torre A", criterio: "Unidad en Torre A", miembros: "320" },
  { id: "sg2", nombre: "Propietarios", criterio: "Tipo = Propietario", miembros: "486" },
  { id: "sg3", nombre: "Miembros Gold", criterio: "Membresía = Gold", miembros: "112" },
];
export const SegmentosSection = () => (
  <CrudSection<Segmento> title="Segmentos" subtitle="Grupos de destinatarios"
    icon="ri-group-2-line" columns={segmentoColumns} fields={segmentoFields} seed={segmentoSeed} searchKey="nombre" />
);

// --- Canales (list) --------------------------------------------------------
interface Canal extends Record<string, unknown> {
  id: string; canal: string; proveedor: string; estado: string;
}
const canalColumns: CrudColumn<Canal>[] = [
  { key: "canal", label: "Canal" },
  { key: "proveedor", label: "Proveedor" },
  { key: "estado", label: "Estado", align: "center", render: (r) => <StatusTextBadge value={r.estado} /> },
];
const canalSeed: Canal[] = [
  { id: "cn1", canal: "Push (app)", proveedor: "Firebase", estado: "Activo" },
  { id: "cn2", canal: "SMS", proveedor: "Twilio", estado: "Activo" },
  { id: "cn3", canal: "Email", proveedor: "SendGrid", estado: "Inactivo" },
];
export const CanalesSection = () => (
  <CrudSection<Canal> title="Canales" subtitle="Canales de comunicación disponibles"
    icon="ri-base-station-line" columns={canalColumns} fields={[]} seed={canalSeed} searchKey="canal" readOnly />
);
